import { ColDef, GridApi, GridReadyEvent, ValueGetterParams } from 'ag-grid-community';
import { useAtomValue } from 'jotai';
import { useCallback, useMemo, useState } from 'react';

import { AgTable } from '../../../components/AgTable/AgTable';
import { RoundFromInvestmentCellRenderer } from '../../../components/AgTable/cell-renderers/RoundCellRenderer';
import { useExtendedExportSettings } from '../../../components/AgTable/exportToExcelDefs';
import { FadeInGridWrapper } from '../../../components/grid-renderers/FadeInGridWrapper';
import { ITransactionDataModel } from '../../../data-models/transaction.data-model';
import { commonTransactionGridSchema } from '../../../schemas/AbstractTransactionSchema';
import { useGetTransactionGridColdDefsForTypeId } from '../../../schemas/transactions/TransactionSchemaUtil';
import { fundsByIdMapAtom, roundsByIdMapAtom } from '../../../services/state/AppConfigStateJ';
import { withOverrides } from '../../../util/ag-grid-utils';
import { genericComparator } from '../../../util/comparators';
import { isPrivateEquityClient } from '../../../util/preference-utils';
import { useSchemaToGrid } from '../../../util/schema-utils';
import { eqBuyGridSchema } from '../../Finance/components/TransactionModal/Forms/schemas/Equity/EquityBuySchema';
import { genericKeyCreator, getMenuColDef } from '../common-grid-defs/commonColDefs';
import { defaultColDef } from '../common-grid-defs/defaultColDefs';
import { allInvestmentRoundsMapStateJ } from '../state/FinanceStateJ';
import { useCommonTransactionGridDefs } from '../Transactions3/Transactions3';
import { IExtendedTransactionDataModel } from './useAllTransactionsData';
import { allTransactionsFundColDef } from './allTransactionsFundColDef';
import { useAllTransactionsGridFilters } from './useAllTransactionsGridFilters';

export interface IAllTransactionsGridProps {
  transactions?: ITransactionDataModel[];
}
export function AllTransactionsGrid({ transactions }: IAllTransactionsGridProps) {
  const [gridApi, setGridApi] = useState<GridApi | null>(null);
  const { doesExternalFilterPass, isExternalFilterPresent, selectedTypes } =
    useAllTransactionsGridFilters(gridApi);
  const schemaToGrid = useSchemaToGrid();
  const investmentsById = useAtomValue(allInvestmentRoundsMapStateJ);
  const roundsById = useAtomValue(roundsByIdMapAtom);
  const fundsById = useAtomValue(fundsByIdMapAtom);
  const getSchemaForTransactionTypeId = useGetTransactionGridColdDefsForTypeId();
  const { getRowId } = useCommonTransactionGridDefs();

  const onGridReady = useCallback((event: GridReadyEvent) => {
    setGridApi(event.api);
  }, []);

  const { exportParams } = useExtendedExportSettings(
    new Set(['companyId', 'fundId', 'transactionTypeId', 'roundId']),
    'All Transactions'
  );

  const initialGridSettings = useMemo(() => {
    let defs = schemaToGrid(eqBuyGridSchema().concat(commonTransactionGridSchema()), [
      'companyId',
      'transactionDate',
      'fundId',
      'transactionTypeId',
      'investmentName',
      'assignmentStatus',
      'status',
      'id',
    ]);
    defs.push(getMenuColDef());

    if (!isPrivateEquityClient()) {
      // Placed by name, not by a counted index: the stage belongs beside the round the transaction
      // is in, and a hardcoded position silently moves it every time a column is added.
      const afterRound = defs.findIndex((d) => (d as ColDef).field === 'investmentName');
      defs.splice(afterRound === -1 ? 5 : afterRound + 1, 0, {
        headerName: 'Stage',
        valueGetter: (params: ValueGetterParams) => {
          const investment = investmentsById.get(params.data?.investmentRoundId ?? -1);
          if (!investment) {
            return '';
          }
          const round = roundsById.get(investment.roundId ?? -1);
          return round?.name ?? '';
        },
        cellRenderer: RoundFromInvestmentCellRenderer,
        keyCreator: genericKeyCreator,
        enableRowGroup: true,
      });
    }

    if (selectedTypes.size === 1) {
      const companyCol = defs[0];
      defs = getSchemaForTransactionTypeId([...selectedTypes.values()][0]) as ColDef[];
      defs.unshift(companyCol);
    }

    const customDefs: Record<string, ColDef<IExtendedTransactionDataModel>> = {
      transactionDate: {
        sortable: true,
        initialSort: 'desc',
        sortIndex: 0,
      },
      fundId: allTransactionsFundColDef(fundsById),
      transactionTypeId: {
        filter: false,
      },
      id: {
        hide: true,
        sortable: true,
        sort: 'asc',
        sortIndex: 1,
      },
    };

    const columnDefs = withOverrides(defs, customDefs);

    return {
      columnDefs,
      defaultColDefs: { ...defaultColDef, minWidth: 150 },
      autoGroupColumnDef: { comparator: genericComparator },
    };
  }, [fundsById, getSchemaForTransactionTypeId, investmentsById, roundsById, schemaToGrid, selectedTypes]);

  return (
    <>
      <div style={{ paddingTop: '1rem', height: '72vh' }}>
        {
          <FadeInGridWrapper key={[...selectedTypes.values()].join(',')}>
            <AgTable
              columnDefs={initialGridSettings.columnDefs}
              rowData={transactions}
              defaultColDef={initialGridSettings.defaultColDefs}
              autoGroupColumnDef={initialGridSettings.autoGroupColumnDef}
              onGridReady={onGridReady}
              doesExternalFilterPass={doesExternalFilterPass}
              isExternalFilterPresent={isExternalFilterPresent}
              defaultExcelExportParams={exportParams}
              defaultCsvExportParams={exportParams}
              getRowId={getRowId}
              suppressScrollOnNewData
              grandTotalRow='bottom'
            />
          </FadeInGridWrapper>
        }
      </div>
    </>
  );
}
